import { useState } from "react";
import { ContractIntegrationStore as Integration } from "store";
import AutoCompleteField, { TOption } from "../../UI/Fields/AutoCompleteField";

import { lang } from "i18nLang";

interface IData {
  client?: TOption;
  branch?: TOption;
  paymentCondition?: TOption;
  documentType?: TOption;
  costCenter?: TOption;
  projectErp?: TOption;
  indicator?: TOption;
}

interface IProps {
  /** Dados do formulário. */
  data: IData;
  /** Objeto de validação usado no formulário. */
  validated: any;
  /** Define se todos os campos são requeridos, com exceção do Indicador. */
  allRequired?: boolean;
  showIndicators?: boolean;
  /** Evento disparado ao alterar um dos campos. */
  onInputChange: (name: string, value: TOption) => void;
  /** Evento disparado ao sair do campo. */
  onInputBlur: (ev: any) => void;
}

/** Formulário com todos os campos e funções para manipulação das informações
 * oriundas da integração.
 */
const FormIntegration = ({
  data,
  validated,
  allRequired = false,
  showIndicators = false,
  onInputChange,
  onInputBlur,
}: IProps) => {
  const [loadingDocumentType, setLoadingDocumentType] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);

  function getDocumentTypesOptions(branchId) {
    setLoadingDocumentType(true);
    Integration.fetchAndSetDocumentTypes(branchId).finally(() =>
      setLoadingDocumentType(false)
    );
  }

  function getClientsOptions(branchId) {
    setLoadingClient(true);
    Integration.fetchAndSetClients(branchId).finally(() =>
      setLoadingClient(false)
    );
  }

  const handleChangeAutoComplete = (name: string, value) => {
    onInputChange(name, value);
  };

  const handleChangeBranch = (name, value: any) => {
    onInputChange("branch", value);
    getClientsOptions(value?.id);
    getDocumentTypesOptions(value?.id);
  };

  const handleFilterOptionsClient = (options, { inputValue }) => {
    if (!inputValue) return options;
    const name = inputValue.toLowerCase();
    return options.filter((opt) => opt.name.toLowerCase().startsWith(name));
  };

  return (
    <>
      <AutoCompleteField
        name="branch"
        label={lang("fields.branch")}
        options={Integration.branchs}
        fullWidth
        value={data.branch}
        onChangeOption={handleChangeBranch}
        onBlur={onInputBlur}
        InputProps={{
          margin: "normal",
          error: !validated.branch.valid,
          helperText: validated.branch.text,
        }}
        required={true}
      />
      <AutoCompleteField
        name="client"
        label={lang("fields.client")}
        options={Integration.clients}
        filterOptions={handleFilterOptionsClient}
        fullWidth
        value={data.client}
        onChangeOption={handleChangeAutoComplete}
        loading={loadingClient || Integration.loadingClients}
        onBlur={onInputBlur}
        InputProps={{
          margin: "normal",
          onKeyDown: (ev) => ev.stopPropagation(),
          error: !validated.client.valid,
          helperText: validated.client.text,
        }}
        required={true}
      />
      <AutoCompleteField
        name="projectErp"
        label={lang("fields.projectErp")}
        options={Integration.projectsErp}
        fullWidth
        value={data.projectErp}
        onChangeOption={handleChangeAutoComplete}
        onBlur={onInputBlur}
        InputProps={{
          margin: "normal",
          onKeyDown: (ev) => ev.stopPropagation(),
          error: allRequired ? !validated.projectErp.valid : false,
          helperText: allRequired ? validated.projectErp.text : undefined,
        }}
        required={allRequired}
      />
      <AutoCompleteField
        name="costCenter"
        label={lang("fields.costCenter")}
        options={Integration.costCenters}
        fullWidth
        value={data.costCenter}
        onChangeOption={handleChangeAutoComplete}
        onBlur={onInputBlur}
        InputProps={{
          margin: "normal",
          error: allRequired ? !validated.costCenter.valid : false,
          helperText: allRequired ? validated.costCenter.text : undefined,
        }}
        required={allRequired}
      />
      <AutoCompleteField
        name="paymentCondition"
        label={lang("fields.paymentCondition")}
        options={Integration.paymentConditions}
        fullWidth
        value={data.paymentCondition}
        onChangeOption={handleChangeAutoComplete}
        onBlur={onInputBlur}
        InputProps={{
          margin: "normal",
          error: allRequired ? !validated.paymentCondition.valid : false,
          helperText: allRequired ? validated.paymentCondition.text : undefined,
        }}
        required={allRequired}
      />
      <AutoCompleteField
        name="documentType"
        label={lang("fields.documentType")}
        options={Integration.documentTypes}
        fullWidth
        value={data.documentType}
        onChangeOption={handleChangeAutoComplete}
        loading={loadingDocumentType}
        onBlur={onInputBlur}
        InputProps={{
          margin: "normal",
          error: allRequired ? !validated.documentType.valid : false,
          helperText: allRequired ? validated.documentType.text : undefined,
        }}
        required={allRequired}
      />
      {showIndicators && (
        <AutoCompleteField
          name="indicator"
          label={lang("fields.indicator")}
          options={Integration.financialIndicators}
          fullWidth
          value={data.indicator}
          onChangeOption={handleChangeAutoComplete}
          onBlur={onInputBlur}
          InputProps={{
            margin: "normal",
          }}
        />
      )}
    </>
  );
};

export default FormIntegration;
